import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, AfterViewInit, Input, Inject, Optional, Output, EventEmitter } from '@angular/core';

import { Subscription, filter, map } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Forms/enums/table-column-input-type.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { DateHelper } from '../../../Forms/helpers/date.helper';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DistributionPickerService } from '../../services/distribution-picker.service';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { DistributionStatus } from '@Common/TargetingShared/enum/distribution-status.enum';

export class DataRow {
    id?: number;
    name?: string;
    count?: number;
    selected?: boolean;
    originalData: DistributionSummaryModel;
    createdUser?: string;
    createdDate?: string; // Formatted
    modifiedUser?: string;
    modifiedDate?: string; // Formatted
}

@Component({
    selector: 'agpr-dist-picker-dialog',
    templateUrl: './dist-picker-dialog.component.html',
    styleUrls: ['./dist-picker-dialog.component.scss'],
    providers: [
        {
            // has a fresh context for adding from anywhere
            // TODO : make it not create this if a context service is provided
            provide: 'AdHocDistPickerService',
            useClass: DistributionPickerService
        }
    ],
    standalone: false
})
export class DistPickerDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() addMode: boolean = true;

    @Output() newSelectedDists: EventEmitter<DistributionSummaryModel[]> = new EventEmitter<DistributionSummaryModel[]>();

    @ViewChild('countInfoCell') public countCellTemplate: TemplateRef<any>;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    subscriptions: Subscription[] = [];

    id = 'distPickerDialog';
    header: string = 'Discover.distributionBuilder.dialog.header';
    loading: boolean = false;

    dataRows: DataRow[];
    filteredRows: DataRow[] = [];
    freshCreatedId: number;
    workingDistsChips: ChipModel[] = [];

    addToDistBuilder = false;
    distPickerService: DistributionPickerService;

    selectedEntities: TargetingResultModel[];
    selectedCount: number = 0;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.checkbox,
        heading: 'Discover.distributionBuilder.table.distributionName',
        labelField: 'name',
        field: 'selected',
        sortable: true,
        sortType: 'text',
        sortField: 'name',
        width: 195
    };
    columnDefs: TableColumnModel[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public usersService: UsersService,
        public distributionService: DistributionService,
        @Optional() public contextDistPickerService: DistributionPickerService,
        @Inject('AdHocDistPickerService') adHocDistPickerService: DistributionPickerService
    ) {
        super(coreDialogService, 'DistPicker');

        this.distPickerService = !!contextDistPickerService ? contextDistPickerService : adHocDistPickerService;

        this.subscriptions.push(

            this.distributionService.getDistributionSummaries().pipe(
                filter(x => !!x),
                map(dist => dist.filter(element => element.status === DistributionStatus.draft))
            ).subscribe(dists => {
                this.dataRows = dists.map(dist => {
                    return {
                        id: dist.id,
                        selected: !!this.workingDistsChips.find(x => x.label === dist.name),
                        name: dist.name,
                        // count: list.count,
                        originalData: dist,
                        createdDate: DateHelper.toDateString(dist.createdDate),
                        modifiedDate: DateHelper.toDateString(dist.modifiedDate),
                    };
                });
                this.filteredRows = this.dataRows;

                this.fetchUserNames();

                // Now that the creation completed and we received the new list,
                // Auto select the list that the user just created
                if (this.freshCreatedId) {
                    const freshCreatedRow = this.dataRows.find(x => x.id === this.freshCreatedId);
                    freshCreatedRow.selected = true;
                    this.workingDistsChips.push({
                        label : freshCreatedRow.name
                    });
                    this.freshCreatedId = undefined;
                }
            }),
            this.distPickerService.getWorkingDistributions().subscribe(lists => {
                this.workingDistsChips = lists.map(x => ({ label: x.name }));
                this.populateDialogFromInitialSelection();
            }),
            this.distPickerService.getSelectedEntities().subscribe(entities => {
                this.selectedEntities = entities;
            }),
            this.distPickerService.getTotal().subscribe(total => {
                this.selectedCount = total;
            })
        );
    }

    ngOnInit() {
        if (!this.addMode) {
            this.header = 'Discover.distributionBuilder.dialog.selectMode.header';
        }
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.columnDefs = [this.nameColumn];
        });
    }

    private populateDialogFromInitialSelection() {
        if (this.dataRows && this.workingDistsChips.length > 0) {
            this.workingDistsChips.forEach(workingDist => {
                const list = this.dataRows.find(x => x.name === workingDist.label);
                if (list) {
                    list.selected = true;
                }
            });
        }
    }

    /** Used when addMode set to false, emitting selected lists */
    selectDistributions() {
        const selectedDists: DistributionSummaryModel[] = this.dataRows
            .filter(x => x.selected)
            .map(x => x.originalData);

        this.distPickerService.setWorkingDistributions(selectedDists);
        this.newSelectedDists.emit(selectedDists);
        this.closeDialog();
    }

    /** Used when addMode set to true, adding contacts to lists depending on addImmediately flag */
    updateDistributions() {
        this.loading = true;
        let addComplete: Promise<void>;

        const selectedDists: DistributionSummaryModel[] = this.dataRows
            .filter(x => x.selected)
            .map(x => x.originalData);

        if (this.addToDistBuilder) {
            this.distPickerService.setWorkingDistributions(selectedDists);
            this.distPickerService.setWorkingDistributionsLock(true);
            addComplete = this.distPickerService.addSelectedEntitiesToWorkingDists();
        } else {
            addComplete = this.distPickerService.addSelectedEntitiesToDists(selectedDists);
        }

        addComplete.then(
            done => {
                this.loading = false;
                this.closeDialog();
            },
            failed => {
                this.loading = false;
                this.closeDialog();
            });
    }

    selectionChanged(item: DataRow) {
        if (item.selected) {
            this.workingDistsChips.push(<ChipModel>{ label: item.name });
        } else {
            this.workingDistsChips.splice(this.workingDistsChips.findIndex(x => x.label === item.name), 1);
        }
        this.workingDistsChips = this.workingDistsChips.slice();
    }

    selectAllChanged(allSelected: boolean) {
        this.workingDistsChips = [];

        if (allSelected) {
            this.dataRows.forEach(row => {
                this.workingDistsChips.push(<ChipModel>{ label: row.name });
            });
        }
    }

    removeChip(index: number) {
        const chipName = this.workingDistsChips[index]?.label;
        this.workingDistsChips.splice(index, 1);
        this.dataRows.find(x => x.name === chipName).selected = false;
    }

    resetDists() {
        this.filteredRows = this.dataRows;
    }

    filterRows(searchString: string) {
        if (searchString.length === 0) {
            this.filteredRows = this.dataRows;
        }
        this.filteredRows = this.dataRows.filter(row => row.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
    }

    /**
     * Converts user ids to usernames for the info tooltip
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId), ...this.dataRows.map(x => +x.originalData.modifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdUser = creator.getDisplayName();
                    }

                    const modifier = users.find(x => x.id === row.originalData.modifiedByUserId);
                    if (modifier) {
                        row.modifiedUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
